/* LOCAL PROJECT STORAGE, FOLDER BACKED — chosen once via showDirectoryPicker,
   then every project is its own .json file in that folder: visible,
   draggable, backed up through whatever the user already syncs that folder
   with, and not lost the moment they clear site data or switch browsers —
   unlike projects-db.js's IndexedDB, which is invisible browser-managed
   storage tied to one origin in one profile.

   This is the module services/projects.js actually imports for "local"
   projects — same function names as projects-db.js on purpose, so nothing
   above this had to change. It falls back to projects-db.js whenever
   there's nothing to fall forward to: the File System Access API doesn't
   exist in this browser (Firefox/Safari — projects-db.js was the only
   option before this and stays the only option there), or the user hasn't
   chosen a folder yet. The moment a folder IS chosen, chooseFolder()
   migrates every record already sitting in IndexedDB into it and clears
   that store — one local source of truth going forward, not two that can
   silently drift apart.

   The chosen folder's own handle is the one thing that still needs
   IndexedDB — a FileSystemDirectoryHandle is structured-cloneable, so a
   single-row store is enough to remember it across reloads. What it can't
   remember is WRITE PERMISSION: Chrome re-asks for that after enough time
   has passed or the browser has restarted, and re-asking requires a real
   click (a page load can't silently reacquire it) — folderStatus()
   reports that case as 'reconnect-needed' rather than quietly falling back
   to IndexedDB, which would look like every local project had vanished. */

import * as db from './projects-db.js';

export const isFsSupported = () => typeof window !== 'undefined' && !!window.showDirectoryPicker;

const HANDLE_DB = 'illusio-projects-folder';
const HANDLE_STORE = 'handle';
const HANDLE_KEY = 'dir';

function openHandleDB(){
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(HANDLE_DB, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(HANDLE_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function getStoredHandle(){
  const idb = await openHandleDB();
  return new Promise((resolve, reject) => {
    const r = idb.transaction(HANDLE_STORE, 'readonly').objectStore(HANDLE_STORE).get(HANDLE_KEY);
    r.onsuccess = () => resolve(r.result || null);
    r.onerror = () => reject(r.error);
  });
}
async function setStoredHandle(handle){
  const idb = await openHandleDB();
  return new Promise((resolve, reject) => {
    const r = idb.transaction(HANDLE_STORE, 'readwrite').objectStore(HANDLE_STORE).put(handle, HANDLE_KEY);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

let dirHandle = null;   // cached once resolved, so a session only hits IndexedDB for it once

export async function folderName(){
  const handle = dirHandle || await getStoredHandle();
  return handle ? handle.name : null;
}

/* A handle read back out of IndexedDB that can't actually be used — the
   object survived but isn't a working FileSystemDirectoryHandle any more.
   Shouldn't happen with a real one, but the alternative to checking is a
   TypeError surfacing to the user as "handle.queryPermission is not a
   function", which tells them nothing and offers no way out. Treated as "no
   folder", which puts the Choose-a-folder button back. */
const usable = h => !!h && typeof h.queryPermission === 'function';

/** 'none' (never configured — IndexedDB is in use), 'granted' (ready), or
 *  'reconnect-needed' (chosen before, but write access needs a fresh click). */
export async function folderStatus(){
  if(!isFsSupported()) return 'none';
  const handle = dirHandle || await getStoredHandle();
  if(!usable(handle)) return 'none';
  dirHandle = handle;
  const perm = await handle.queryPermission({ mode: 'readwrite' });
  return perm === 'granted' ? 'granted' : 'reconnect-needed';
}

/** Only null when no folder has ever been chosen — the "use IndexedDB"
 *  case. A chosen-but-not-yet-permitted folder throws instead of silently
 *  falling back, so a reconnect prompt is what shows up, not an empty list
 *  that looks like every project was deleted. */
async function activeHandle(){
  if(!isFsSupported()) return null;
  const handle = dirHandle || await getStoredHandle();
  if(!usable(handle)) return null;          // fall back to IndexedDB, don't throw
  dirHandle = handle;
  const perm = await handle.queryPermission({ mode: 'readwrite' });
  if(perm !== 'granted') throw new Error('Reconnect the projects folder before doing that.');
  return handle;
}

/** Re-request permission on the ALREADY-chosen folder. Must be called from
 *  a click — browsers refuse to prompt otherwise. */
export async function reconnectFolder(){
  const handle = dirHandle || await getStoredHandle();
  if(!handle) throw new Error('No folder has been chosen yet.');
  const perm = await handle.requestPermission({ mode: 'readwrite' });
  if(perm !== 'granted') throw new Error('Permission wasn’t granted.');
  dirHandle = handle;
}

/**
 * Open the folder picker and remember the choice. The first time this
 * runs, whatever was already in IndexedDB is copied in and then cleared —
 * later calls (picking a DIFFERENT folder) find nothing left to migrate,
 * so this doubles as "change folder" with no separate function needed.
 * Must be called from a click.
 */
export async function chooseFolder(){
  const handle = await window.showDirectoryPicker({ id: 'illusio-projects', mode: 'readwrite' });
  const perm = await handle.requestPermission({ mode: 'readwrite' });
  if(perm !== 'granted') throw new Error('Permission wasn’t granted.');

  const existing = await db.listLocal();
  for(const record of existing) await writeRecord(handle, record);
  for(const record of existing) await db.deleteLocal(record.id);

  await setStoredHandle(handle);
  dirHandle = handle;
}

/* ── file naming ───────────────────────────────────────────────────────────
   The point of a folder is that you can look in it, so the file has to be
   called what the project is called — these used to be named by their id,
   which meant a directory full of `a3f2b1c8-….json`. The id stays the stable
   key (it lives INSIDE the file), and the name is just a label: renaming a
   project renames its file, and nothing looks a project up by filename. */

/* Windows is the strict one: it rejects \ / : * ? " < > |, refuses trailing
   dots or spaces, and reserves a handful of device names whatever the
   extension. Everything here is safe on macOS and Linux too. */
const RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
export function safeFileName(title){
  let s = String(title || '')
    // Illegal on Windows, plus control characters. Spaces are deliberately
    // KEPT — "My Signage.json" is the entire point of this.
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[.\s]+|[.\s]+$/g, '');             // no leading/trailing dot or space
  if(RESERVED.test(s)) s = s + '-project';
  if(s.length > 80) s = s.slice(0, 80).trim();    // keep well clear of path limits
  return (s || 'Untitled') + '.json';
}

/** A name not already taken by a DIFFERENT file — "Signage.json",
 *  "Signage 2.json", … Two projects are allowed to share a title; two files
 *  in one folder are not. */
async function uniqueName(handle, wanted, keepName){
  const taken = new Set();
  for await (const [name, entry] of handle.entries()){
    if(entry.kind === 'file' && name !== keepName) taken.add(name.toLowerCase());
  }
  if(!taken.has(wanted.toLowerCase())) return wanted;
  const base = wanted.replace(/\.json$/i, '');
  for(let n = 2; ; n++){
    const candidate = `${base} ${n}.json`;
    if(!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/* ── record CRUD — same shape projects-db.js's IndexedDB rows always had,
   read from files instead of object-store entries ─────────────────────── */

async function readRecord(handle, name){
  const fh = await handle.getFileHandle(name);
  const file = await fh.getFile();
  return JSON.parse(await file.text());
}

/** Find a project's file by the id stored inside it, since the filename is
 *  the (renameable, user-facing) title rather than the key. Also what keeps
 *  folders written by the earlier id-named version working untouched. */
async function findEntry(handle, id){
  for await (const [name, entry] of handle.entries()){
    if(entry.kind !== 'file' || !name.endsWith('.json')) continue;
    try {
      const record = await readRecord(handle, name);
      if(record && record.id === id) return { name, record };
    } catch { /* not a project file — skip */ }
  }
  return null;
}

/** Writes the record under a filename matching its title, moving it off its
 *  previous name when the title changed (or when the file was still named by
 *  id from the older scheme). */
async function writeRecord(handle, record, currentName){
  const wanted = await uniqueName(handle, safeFileName(record.title), currentName);
  const fh = await handle.getFileHandle(wanted, { create: true });
  const w = await fh.createWritable();
  await w.write(JSON.stringify(record, null, 2));
  await w.close();
  if(currentName && currentName !== wanted){
    try { await handle.removeEntry(currentName); } catch { /* already gone */ }
  }
  return record;
}

export async function listLocal(){
  const handle = await activeHandle();
  if(!handle) return db.listLocal();
  const rows = [];
  for await (const [name, entry] of handle.entries()){
    if(entry.kind !== 'file' || !name.endsWith('.json')) continue;
    try { rows.push(await readRecord(handle, name)); }
    catch { /* not a valid project file — skip it rather than fail the whole list */ }
  }
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getLocal(id){
  const handle = await activeHandle();
  if(!handle) return db.getLocal(id);
  const found = await findEntry(handle, id);
  return found ? found.record : null;
}

const notFound = () => { throw new Error('That project no longer exists — it may have been deleted in another tab.'); };

export async function saveLocal(title, doc){
  const handle = await activeHandle();
  if(!handle) return db.saveLocal(title, doc);
  const now = Date.now();
  return writeRecord(handle, { id: crypto.randomUUID(), title, doc, createdAt: now, updatedAt: now });
}

export async function overwriteLocal(id, doc){
  const handle = await activeHandle();
  if(!handle) return db.overwriteLocal(id, doc);
  const found = await findEntry(handle, id);
  if(!found) notFound();
  return writeRecord(handle, { ...found.record, doc, updatedAt: Date.now() }, found.name);
}

export async function renameLocal(id, title){
  const handle = await activeHandle();
  if(!handle) return db.renameLocal(id, title);
  const found = await findEntry(handle, id);
  if(!found) notFound();
  return writeRecord(handle, { ...found.record, title, updatedAt: Date.now() }, found.name);
}

export async function duplicateLocal(id){
  const handle = await activeHandle();
  if(!handle) return db.duplicateLocal(id);
  const found = await findEntry(handle, id);
  if(!found) notFound();
  const now = Date.now();
  return writeRecord(handle,
    { ...found.record, id: crypto.randomUUID(), title: found.record.title + ' copy', createdAt: now, updatedAt: now });
}

export async function deleteLocal(id){
  const handle = await activeHandle();
  if(!handle) return db.deleteLocal(id);
  /* By id, not by `id + '.json'` — the filename is the project's TITLE now,
     and a renamed project's file no longer matches its id at all. Getting
     this wrong deleted nothing and reported success. */
  const found = await findEntry(handle, id);
  if(!found) return;                              // already gone
  try { await handle.removeEntry(found.name); } catch { /* already gone */ }
}
